import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { localInput, remindPresets } from "../reminders";
import { PopMenu } from "./PopMenu";
import { Field, Form, Sheet } from "./ui";
import { t } from "../i18n";

// TimePicker asks for a time to come: one of the usual ones from a menu by
// the button that asked, or any from a sheet. Without an anchor it opens
// the sheet at once.
export function TimePicker({
  anchor,
  label,
  title,
  action,
  children,
  onPick,
  onClose,
}: {
  anchor?: DOMRect;
  label: string;
  title: string;
  action: string;
  children?: ReactNode;
  onPick: (at: Date) => void;
  onClose: () => void;
}) {
  const [custom, setCustom] = useState(!anchor);
  // Picking "Custom time…" closes the menu but not the picker.
  const toSheet = useRef(false);
  if (custom || !anchor) {
    return (
      <TimeSheet title={title} action={action} onClose={onClose} onPick={(at) => (onClose(), onPick(at))}>
        {children}
      </TimeSheet>
    );
  }
  return (
    <PopMenu
      anchor={anchor}
      label={label}
      onClose={() => toSheet.current || onClose()}
      items={[
        ...remindPresets().map((p) => ({ label: p.label, run: () => onPick(p.at) })),
        { label: t("Custom time…"), run: () => ((toSheet.current = true), setCustom(true)) },
      ]}
    />
  );
}

export function TimeSheet({
  title,
  action,
  children,
  focusTime = true,
  onPick,
  onClose,
}: {
  title: string;
  action: string;
  children?: ReactNode;
  focusTime?: boolean;
  onPick: (at: Date) => void;
  onClose: () => void;
}) {
  const hour = new Date(Date.now() + 60 * 60_000);
  hour.setMinutes(0, 0, 0);
  const [when, setWhen] = useState(localInput(hour));
  const at = new Date(when);
  const ok = !Number.isNaN(at.getTime()) && at.getTime() > Date.now();
  function submit(e: FormEvent) {
    e.preventDefault();
    if (ok) onPick(at);
  }
  return (
    <Sheet title={title} onClose={onClose} className="time-sheet">
      <Form onSubmit={submit}>
        {children}
        <Field label={t("When")}>
          <input type="datetime-local" value={when} min={localInput(new Date())} onChange={(e) => setWhen(e.target.value)} autoFocus={focusTime} required />
        </Field>
        <div className="actions">
          <button type="submit" disabled={!ok}>
            {action}
          </button>
        </div>
      </Form>
    </Sheet>
  );
}
